import { Badge, Button, Group, Paper, Stack, Text, Textarea, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { LoadingOverlay } from "../components/ui";
import { adminApi, disputesApi } from "../lib/api";
import { ApiError } from "../lib/apiClient";
import {
  DISPUTE_STATUS_LABELS,
  DISPUTE_TYPE_LABELS,
  disputeStatusColor,
  formatDate,
} from "../lib/format";

interface Props {
  basePath?: string;
  admin?: boolean;
}

export function DisputeDetailPage({ basePath = "/disputes", admin = false }: Props) {
  const { disputeId } = useParams<{ disputeId: string }>();
  const queryClient = useQueryClient();
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");

  const disputeQuery = useQuery({
    queryKey: ["dispute", disputeId],
    queryFn: () => disputesApi.get(disputeId!),
    enabled: !!disputeId,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["dispute", disputeId] });
    queryClient.invalidateQueries({ queryKey: ["disputes"] });
    queryClient.invalidateQueries({ queryKey: ["admin-disputes"] });
  };

  const investigateMutation = useMutation({
    mutationFn: () => adminApi.investigateDispute(disputeId!, { internal_notes: internalNotes || undefined }),
    onSuccess: () => {
      notifications.show({ color: "blue", title: "Investigating", message: "Parties have been notified." });
      invalidate();
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        title: "Failed",
        message: err instanceof ApiError ? err.message : "Something went wrong",
      });
    },
  });

  const resolveMutation = useMutation({
    mutationFn: () =>
      adminApi.resolveDispute(disputeId!, {
        resolution_notes: resolutionNotes,
        internal_notes: internalNotes || undefined,
      }),
    onSuccess: () => {
      notifications.show({ color: "green", title: "Resolved", message: "Parties have been notified." });
      invalidate();
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        title: "Failed",
        message: err instanceof ApiError ? err.message : "Something went wrong",
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () =>
      adminApi.rejectDispute(disputeId!, {
        resolution_notes: resolutionNotes,
        internal_notes: internalNotes || undefined,
      }),
    onSuccess: () => {
      notifications.show({ color: "gray", title: "Rejected", message: "Parties have been notified." });
      invalidate();
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        title: "Failed",
        message: err instanceof ApiError ? err.message : "Something went wrong",
      });
    },
  });

  if (disputeQuery.isLoading) {
    return <LoadingOverlay height="var(--axio-unit-300)" />;
  }

  const dispute = disputeQuery.data;
  if (!dispute) return <Text c="dimmed">Dispute not found.</Text>;

  const open = dispute.status === "open" || dispute.status === "investigating";
  const ctx = dispute.context;

  return (
    <Stack gap="lg">
      <Button variant="subtle" component={Link} to={basePath} w="fit-content" px="var(--axio-unit-0)">
        ← Back to disputes
      </Button>

      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={2}>{DISPUTE_TYPE_LABELS[dispute.dispute_type] ?? dispute.dispute_type}</Title>
          <Text c="dimmed">Opened {formatDate(dispute.created_at)}</Text>
        </div>
        <Badge size="lg" color={disputeStatusColor(dispute.status)} variant="light">
          {DISPUTE_STATUS_LABELS[dispute.status] ?? dispute.status}
        </Badge>
      </Group>

      <Paper withBorder p="md">
        <Title order={5} mb="sm">
          Parties
        </Title>
        <Text size="sm">Raised by: {dispute.raised_by_name ?? "—"}</Text>
        <Text size="sm">Against: {dispute.against_name ?? "—"}</Text>
      </Paper>

      <Paper withBorder p="md">
        <Title order={5} mb="sm">
          Context
        </Title>
        <Text size="sm">Candidate: {ctx?.candidate_name ?? "—"}</Text>
        <Text size="sm">Job: {ctx?.job_title ?? "—"}</Text>
        <Text size="sm">Company: {ctx?.company_name ?? "—"}</Text>
        <Text size="sm">Submission status: {ctx?.submission_status ?? "—"}</Text>
        {ctx?.placement_status && <Text size="sm">Placement: {ctx.placement_status}</Text>}
        {ctx?.guarantee_until && <Text size="sm">Guarantee until: {formatDate(ctx.guarantee_until)}</Text>}
        {ctx?.payout_status && <Text size="sm">Payout: {ctx.payout_status}</Text>}
        {dispute.submission_id && (
          <Button
            component={Link}
            to={admin ? `/admin/applications` : basePath.startsWith("/hr") ? `/hr/submissions/${dispute.submission_id}` : `/submissions/${dispute.submission_id}`}
            variant="light"
            size="xs"
            mt="sm"
          >
            Open submission
          </Button>
        )}
      </Paper>

      <Paper withBorder p="md">
        <Title order={5} mb="sm">
          Description
        </Title>
        <Text>{dispute.description}</Text>
      </Paper>

      {dispute.resolution_notes && (
        <Paper withBorder p="md">
          <Title order={5} mb="sm">
            Resolution
          </Title>
          <Text>{dispute.resolution_notes}</Text>
          {dispute.resolved_at && (
            <Text size="xs" c="dimmed" mt="xs">
              {formatDate(dispute.resolved_at)}
            </Text>
          )}
        </Paper>
      )}

      {admin && open && (
        <Paper withBorder p="md">
          <Title order={5} mb="sm">
            Mediate (admin)
          </Title>
          <Stack>
            <Textarea
              label="Public resolution notes"
              description="Visible to both parties when you resolve or reject."
              minRows={3}
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.currentTarget.value)}
            />
            <Textarea
              label="Internal notes (optional)"
              description="Admin only."
              minRows={2}
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.currentTarget.value)}
            />
            <Group>
              {dispute.status === "open" && (
                <Button
                  color="blue"
                  loading={investigateMutation.isPending}
                  onClick={() => investigateMutation.mutate()}
                >
                  Mark investigating
                </Button>
              )}
              <Button
                color="green"
                loading={resolveMutation.isPending}
                disabled={resolutionNotes.trim().length < 5}
                onClick={() => resolveMutation.mutate()}
              >
                Resolve
              </Button>
              <Button
                color="red"
                variant="light"
                loading={rejectMutation.isPending}
                disabled={resolutionNotes.trim().length < 5}
                onClick={() => rejectMutation.mutate()}
              >
                Reject
              </Button>
            </Group>
          </Stack>
        </Paper>
      )}

      {admin && dispute.internal_notes && (
        <Paper withBorder p="md" bg="gray.0">
          <Title order={5} mb="sm">
            Internal notes
          </Title>
          <Text size="sm">{dispute.internal_notes}</Text>
        </Paper>
      )}
    </Stack>
  );
}
